import { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, Trash2, KeyRound, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "npd_engineer", unit_id: "" });
  const [saving, setSaving] = useState(false);
  // Round 21.4 — admin password reset modal
  const [pwModal, setPwModal] = useState(null);
  const [pwForm, setPwForm] = useState({ new_password: "", confirm: "" });
  // Edit-user modal
  const [editUser, setEditUser] = useState(null);   // { user_id, name, email, role, unit_id, password }
  const [savingEdit, setSavingEdit] = useState(false);
  const [showEditPw, setShowEditPw] = useState(false);

  function openEdit(u) {
    setShowEditPw(false);
    setEditUser({ user_id: u.user_id, name: u.name || "", email: u.email || "", role: u.role, unit_id: u.unit_id || "", password: "" });
  }
  async function saveEdit() {
    if (!editUser.name.trim() || !editUser.email.trim()) { toast.error("Name and email are required"); return; }
    if (!isCrossUnit(editUser.role) && !editUser.unit_id) { toast.error("Pick a unit for this role"); return; }
    if (editUser.password && editUser.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingEdit(true);
    try {
      const body = {
        name: editUser.name.trim(), email: editUser.email.trim(), role: editUser.role,
        unit_id: isCrossUnit(editUser.role) ? null : editUser.unit_id,
      };
      if (editUser.password) body.password = editUser.password;
      await api.put(`/users/${editUser.user_id}`, body);
      toast.success("User updated");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSavingEdit(false); }
  }
  const [pwError, setPwError] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, un, r] = await Promise.all([api.get("/users"), api.get("/units"), api.get("/roles")]);
      setUsers(u.data || []);
      setUnits(un.data || []);
      setRoles(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  if (user?.role !== "admin") {
    return <div className="p-6 text-slate-500">User management is admin-only.</div>;
  }

  const roleById = Object.fromEntries(roles.map((r) => [r.role_id, r]));
  const isCrossUnit = (roleId) => !!roleById[roleId]?.cross_unit;

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email, password required"); return; }
    if (!isCrossUnit(form.role) && !form.unit_id) { toast.error("Pick a unit for this role"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "npd_engineer", unit_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const updateUnit = async (u, newUnitId) => {
    try {
      await api.put(`/users/${u.user_id}`, { unit_id: newUnitId });
      toast.success(`Reassigned ${u.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const updateRole = async (u, newRole) => {
    try {
      await api.put(`/users/${u.user_id}`, { role: newRole });
      toast.success(`Role updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (u) => {
    if (u.user_id === user.user_id) { toast.error("Cannot delete yourself"); return; }
    if (!window.confirm(`Delete user ${u.email}? Their NPDs will be reassigned to you.`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  function openPwModal(u) {
    setPwModal({ user_id: u.user_id, name: u.name, email: u.email });
    setPwForm({ new_password: "", confirm: "" });
    setPwError("");
  }
  function closePwModal() {
    setPwModal(null);
    setPwForm({ new_password: "", confirm: "" });
    setPwError("");
  }
  async function savePw() {
    if (pwForm.new_password.length < 8) {
      setPwError("Password must be at least 8 characters.");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError("Passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post(`/users/${pwModal.user_id}/reset-password`, { new_password: pwForm.new_password });
      closePwModal();
      toast.success("Password updated");
    } catch (e) {
      setPwError(e.response?.data?.detail || e.message);
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="users-admin-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <UsersIcon size={22} className="text-orange-600" /> Users
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage users and their unit assignment. Admin only.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Add New User</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="Name" className="input" data-testid="new-user-name" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="Email" type="email" className="input" data-testid="new-user-email" />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 placeholder="Password (min 6)" type="password" className="input" data-testid="new-user-password" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, unit_id: isCrossUnit(e.target.value) ? "" : form.unit_id })}
                  className="input" data-testid="new-user-role">
            {roles.map((r) => (
              <option key={r.role_id} value={r.role_id}>{r.name}</option>
            ))}
          </select>
          <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                  className="input" disabled={isCrossUnit(form.role)} data-testid="new-user-unit">
            <option value="">{isCrossUnit(form.role) ? "Not required (cross-unit)" : "Select unit…"}</option>
            {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
          </select>
        </div>
        <button onClick={create} disabled={saving}
                className="mt-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md px-4 py-2 flex items-center gap-1.5"
                data-testid="new-user-create">
          <Plus size={14} /> {saving ? "Saving…" : "Add User"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Unit</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50">
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-slate-600">{u.email}</td>
                  <td className="p-3">
                    <select value={u.role} onChange={(e) => updateRole(u, e.target.value)}
                            className="px-2 py-1 border border-slate-200 rounded text-xs bg-white"
                            data-testid={`user-role-${u.email}`}>
                      {roles.map((r) => (
                        <option key={r.role_id} value={r.role_id}>{r.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3">
                    {isCrossUnit(u.role) ? (
                      <span className="text-xs text-slate-400 italic">(all units)</span>
                    ) : (
                      <select value={u.unit_id || ""} onChange={(e) => updateUnit(u, e.target.value)}
                              className="px-2 py-1 border border-slate-200 rounded text-xs bg-white"
                              data-testid={`user-unit-${u.email}`}>
                        <option value="" disabled>—</option>
                        {units.map((un) => <option key={un.unit_id} value={un.unit_id}>{un.unit_code} — {un.unit_name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => openEdit(u)}
                      className="inline-flex items-center gap-1 text-slate-500 hover:text-orange-600 px-2 py-1 text-xs"
                      title="Edit user"
                      data-testid={`user-edit-${u.email}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => openPwModal(u)}
                      className="inline-flex items-center gap-1 text-slate-500 hover:text-orange-600 px-2 py-1 text-xs"
                      title="Reset password"
                      data-testid={`reset-pw-${u.email}`}
                    >
                      <KeyRound size={14} />
                    </button>
                    {u.user_id !== user.user_id && (
                      <button onClick={() => remove(u)} className="text-slate-400 hover:text-red-600 px-2 py-1" data-testid={`user-delete-${u.email}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="edit-user-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <Pencil size={16} className="text-orange-600" /> Edit User
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Name</label>
                <input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className="input" data-testid="edit-user-name" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Email</label>
                <input value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} type="email" className="input" data-testid="edit-user-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Role</label>
                  <select value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value, unit_id: isCrossUnit(e.target.value) ? "" : editUser.unit_id })} className="input" data-testid="edit-user-role">
                    {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Unit</label>
                  <select value={editUser.unit_id} onChange={(e) => setEditUser({ ...editUser, unit_id: e.target.value })} className="input" disabled={isCrossUnit(editUser.role)} data-testid="edit-user-unit">
                    <option value="">{isCrossUnit(editUser.role) ? "All units" : "Select unit…"}</option>
                    {units.map((un) => <option key={un.unit_id} value={un.unit_id}>{un.unit_code} — {un.unit_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">New password <span className="text-slate-400">(leave blank to keep current)</span></label>
                <div className="relative">
                  <input value={editUser.password} onChange={(e) => setEditUser({ ...editUser, password: e.target.value })} type={showEditPw ? "text" : "password"} placeholder="min 6 chars" className="input pr-9" data-testid="edit-user-password" />
                  <button type="button" onClick={() => setShowEditPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" title={showEditPw ? "Hide" : "Show"} data-testid="edit-user-password-toggle">
                    {showEditPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Stored passwords are encrypted (hashed) and can't be shown — set a new one here to change it.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditUser(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">Cancel</button>
              <button onClick={saveEdit} disabled={savingEdit} className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-semibold rounded-md" data-testid="edit-user-save">
                {savingEdit ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5" data-testid="reset-pw-modal">
            <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
              <KeyRound size={16} className="text-orange-600" /> Reset Password
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              For <span className="font-medium">{pwModal.name}</span> ({pwModal.email})
            </p>
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">New Password (min 8 chars)</label>
              <input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm"
                autoFocus
                data-testid="reset-pw-new"
              />
            </div>
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">Confirm Password</label>
              <input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm"
                data-testid="reset-pw-confirm"
              />
            </div>
            {pwError && (
              <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="reset-pw-error">{pwError}</div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button onClick={closePwModal} disabled={pwBusy} className="px-3 py-1.5 text-sm border border-slate-300 rounded">Cancel</button>
              <button
                onClick={savePw}
                disabled={pwBusy || !pwForm.new_password || !pwForm.confirm}
                className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                data-testid="reset-pw-save"
              >
                {pwBusy ? "Saving…" : "Save Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
